import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * MultiSelect (Iter 116) — seleção múltipla com busca, no mesmo visual do Combobox.
 * options: [{ id, nome, cor?, sub? }] · extras: [{ value, label }] (ex.: "__none__" = Sem rota)
 * value: string[] · onChange(string[]) · placeholder = rótulo quando nada selecionado ("Todos").
 */
export function MultiSelect({ options = [], extras = [], value = [], onChange, placeholder = "Todos", testId, searchable = true, className = "" }) {
  const [open, setOpen] = useState(false);
  const labelOf = useMemo(() => {
    const m = new Map(options.map((o) => [o.id, o.nome]));
    extras.forEach((x) => m.set(x.value, x.label));
    return m;
  }, [options, extras]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const n = value.length;
  const label = n === 0 ? placeholder : n === 1 ? (labelOf.get(value[0]) || "1 selecionado") : `${n} selecionados`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId} data-count={n}
          className={cn("w-full justify-between font-normal", n > 0 && "border-orange-400 bg-orange-50/60 text-orange-900", className)}>
          <span className={cn("truncate text-left", n === 0 && "text-muted-foreground")}>{label}</span>
          <span className="ml-2 flex items-center gap-1 shrink-0">
            {n > 0 && (
              <span role="button" tabIndex={-1} title="Limpar" data-testid={`${testId}-clear`}
                onClick={(e) => { e.stopPropagation(); onChange([]); }} className="rounded-full hover:bg-orange-200 p-0.5">
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder="Digite para buscar..." data-testid={`${testId}-input`} />}
          <CommandList>
            <CommandEmpty>Nenhum resultado.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => onChange([])} data-testid={`${testId}-opt-all`}>
                <Check className={cn("mr-2 h-4 w-4", n === 0 ? "opacity-100" : "opacity-0")} />
                <span className={n === 0 ? "font-semibold" : "text-muted-foreground"}>{placeholder}</span>
              </CommandItem>
              {extras.map((x) => (
                <CommandItem key={x.value} value={x.label} onSelect={() => toggle(x.value)} data-testid={`${testId}-opt-${x.value}`} data-checked={value.includes(x.value)}>
                  <Check className={cn("mr-2 h-4 w-4", value.includes(x.value) ? "opacity-100" : "opacity-0")} />
                  <span className="text-muted-foreground">{x.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {(extras.length > 0) && <CommandSeparator />}
            <CommandGroup>
              {options.map((o) => (
                <CommandItem key={o.id} value={o.sub ? `${o.nome} ${o.sub}` : o.nome} onSelect={() => toggle(o.id)} data-testid={`${testId}-opt-${o.id}`} data-checked={value.includes(o.id)}>
                  <Check className={cn("mr-2 h-4 w-4", value.includes(o.id) ? "opacity-100" : "opacity-0")} />
                  {o.cor && <span className="w-2.5 h-2.5 rounded-full shrink-0 mr-2" style={{ backgroundColor: o.cor }} />}
                  <span className="flex-1 truncate">{o.nome}</span>
                  {o.sub && <span className="ml-2 text-xs text-muted-foreground shrink-0">{o.sub}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {n > 0 && (
            <div className="border-t p-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground" data-testid={`${testId}-count`}>{n} selecionado(s)</span>
              <button type="button" className="text-orange-700 font-semibold hover:underline" onClick={() => onChange([])}>Limpar</button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
